import { AccessDeviceImportContext } from "@components/sgt/access-devices/AccessDeviceImportContext";
import { Plus, RefreshCw } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekButton, PeekDialog, PeekIconButton, PeekLoader, PeekStack, PeekTypography, } from "@piiqtechnologies/ui-components";
import { useAnalytics, useCreateSeamConnectWebview } from "@piiqtechnologies/ui-lib";
import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import messages from "./messages.i18n";
export const ImportAccessDevices = ({ community }) => {
    const { setIsImporting } = useContext(AccessDeviceImportContext);
    const [showSeamLogin, setShowSeamLogin] = useState(false);
    const intl = useIntl();
    const [createConnectWebview, createdConnectWebview, isCreatingConnectWebview, errorCreatingConnectWebview] = useCreateSeamConnectWebview();
    const [hasUserLoggedIn, setHasUserLoggedIn] = useState(false);
    const analytics = useAnalytics();
    // Event listener for "connect_webview.login_succeeded" event
    // We receive this event when the user has successfully logged in to their smartlock provider
    useEffect(() => {
        const handleMessage = (event) => {
            var _a, _b;
            if (((_b = (_a = event.data) === null || _a === void 0 ? void 0 : _a.event) === null || _b === void 0 ? void 0 : _b.event_type) === "connect_webview.login_succeeded") {
                analytics.track("[Access Device Import] User logged in successfully", { communityId: community._id });
                setHasUserLoggedIn(true);
            }
        };
        window.addEventListener("message", handleMessage, false);
        // Cleanup function removes the listener when the component unmounts
        return () => {
            window.removeEventListener("message", handleMessage, false);
        };
    }, [analytics, community._id]);
    useEffect(() => {
        // Create a new EventSource connection to the server
        const eventSource = new EventSource(`${process.env.PEEK_APP_API_URL}sgt/seam/watch-events/${community._id}`);
        // Track if we're receiving events
        let importInProgress = false;
        let importTimeoutId;
        // Handler for incoming messages
        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if ((data === null || data === void 0 ? void 0 : data.event) === "connected") {
                return;
            }
            // Set importing state to true when events start coming in
            if (!importInProgress) {
                importInProgress = true;
                setIsImporting(true);
            }
            // Clear any existing timeout and set a new one
            if (importTimeoutId) {
                clearTimeout(importTimeoutId);
            }
            // Set a timeout to mark import as complete if no events received for 3 seconds
            importTimeoutId = setTimeout(() => {
                importInProgress = false;
                setIsImporting(false);
                setShowSeamLogin(false);
                analytics.track("[Access Device Import] Import completed", { communityId: community._id });
            }, 800);
        };
        // Handle errors
        eventSource.onerror = (error) => {
            setIsImporting(false);
            setShowSeamLogin(false);
            setHasUserLoggedIn(false);
            analytics.track("[Access Device Import] Error", { communityId: community._id, error: error });
            eventSource.close();
        };
        // Cleanup on component unmount
        return () => {
            if (importTimeoutId) {
                clearTimeout(importTimeoutId);
            }
            setIsImporting(false);
            eventSource.close();
        };
    }, [community._id, setIsImporting]);
    const handleClickImport = () => {
        createConnectWebview({ communityId: community._id });
        setShowSeamLogin(true);
        analytics.track("[Access Device Import] User clicked import button", { communityId: community._id });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekButton, { size: "large", variant: "outlined", color: "info", startIcon: React.createElement(Plus, null), onClick: handleClickImport, sx: { alignSelf: "center", minWidth: "fit-content" }, "data-testid": "create-access-device-button" }, intl.formatMessage(messages.importAccessDevicesButton)),
        !hasUserLoggedIn && (React.createElement(PeekDialog, { disablePortal: true, maxWidth: "sm", hasCloseIcon: true, title: intl.formatMessage(messages.importAccessDevicesButton), open: showSeamLogin, onClose: () => {
                setShowSeamLogin(false);
            }, fullWidth: true },
            (createdConnectWebview && (React.createElement("iframe", { src: createdConnectWebview.url, width: "100%", height: "100%", frameBorder: "0", allowFullScreen: true, title: "Seam Login", sandbox: "allow-scripts allow-same-origin allow-popups allow-forms", style: { display: "block", minHeight: "650px" } }))) ||
                (errorCreatingConnectWebview && (React.createElement(PeekStack, { spacing: 2, direction: "column", alignItems: "center", justifyContent: "center", sx: { minHeight: "650px" } },
                    React.createElement(PeekTypography, { variant: "body1", color: "error" }, (errorCreatingConnectWebview === null || errorCreatingConnectWebview === void 0 ? void 0 : errorCreatingConnectWebview.message) || intl.formatMessage(messages.importAccessDevicesError)),
                    React.createElement(PeekIconButton, { onClick: handleClickImport, "aria-label": intl.formatMessage(messages.importAccessDevicesRetry), sx: { p: 0 }, bordered: false },
                        React.createElement(PeekTypography, { color: "primary.main", lineHeight: 0 },
                            React.createElement(RefreshCw, { height: 18, width: 18 })))))),
            !createdConnectWebview && isCreatingConnectWebview && !errorCreatingConnectWebview && (React.createElement(PeekBox, { sx: { minHeight: "650px", display: "flex", justifyContent: "center", alignItems: "center" } },
                React.createElement(PeekLoader, null)))))));
};
export default ImportAccessDevices;
